import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Divider,
  useTheme,
  IconButton,
  Chip,
  LinearProgress,
  Stack
} from "@mui/material";
import {
  LocalShipping,
  Notifications,
  TrendingUp,
  Inventory,
  ArrowForward,
  Refresh
} from "@mui/icons-material";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SortIcon from "@mui/icons-material/Sort";
import { useNavigate } from "react-router-dom";
import { getMyAccount, getAccountBalance } from "api/wallet";
import {
  ClientGetMyPackage,
  ClientGetCountPackagesByStatus,
  ClientGetMyAccountInfo,
  ClientGetRouteList,
} from "api/client";
import moment from "moment";
import TrackingDetailDialog from "components/TrackingDetialDialog.";

const statusVisual = (status) => {
  switch (status) {
    case "Cancelled":
      return { icon: <CancelIcon fontSize="small" />, color: "error.main" };
    case "Delivered":
      return { icon: <CheckCircleIcon fontSize="small" />, color: "success.main" };
    case "Arrived":
      return { icon: <StorefrontIcon fontSize="small" />, color: "secondary.main" };
    case "Pending":
    case "Inprogress":
    case "In-progress":
    case "In-transit":
      return { icon: <LocalShippingIcon fontSize="small" />, color: "warning.main" };
    case "At Hub":
      return { icon: <SortIcon fontSize="small" />, color: "#7b1fa2" };
    default:
      return { icon: <HourglassEmptyIcon fontSize="small" />, color: "grey.600" };
  }
};

function CustomerPortal() {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State management
  const [accountData, setAccountData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [balance, setBalance] = useState(null);
  const [walletActive, setWalletActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recentPackages, setRecentPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState("");
  const [packageCounts, setPackageCounts] = useState({
    inprogress: 0,
    arrived: 0,
    total: 0,
  });

  // Load account data on component mount
  useEffect(() => {
    const loadAccountData = async () => {
      try {
        setLoading(true);
        const [accountRes, balanceRes, profileRes, routesRes] = await Promise.all([
          getMyAccount(),
          getAccountBalance(),
          ClientGetMyAccountInfo(),
          ClientGetRouteList(),
        ]);

        if (accountRes.success) {
          setAccountData(accountRes.data);
          setWalletActive(!!accountRes.walletActive);
        }
        if (balanceRes.success) setBalance(balanceRes.data);
        if (profileRes?.status === 200 && profileRes.customer) setProfileData(profileRes.customer);
        if (routesRes?.success) setRoutes(routesRes.data || []);
      } catch (error) {
        console.error('Failed to load account data:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadPackagesOverview = async () => {
      try {
        setLoadingPackages(true);
        const [recentRes, countsRes] = await Promise.all([
          ClientGetMyPackage(0, 10, { field: "updatedAt", sort: "desc" }, "", undefined),
          ClientGetCountPackagesByStatus(),
        ]);

        if (recentRes && recentRes.status === 200) {
          setRecentPackages(recentRes.packages || []);
        }
        if (countsRes && countsRes.status === 200) {
          // Backend returns { status, packages: [{ _id: "<status>", count }] }
          const groups = countsRes.packages || [];
          const map = groups.reduce((acc, g) => {
            acc[g._id] = g.count || 0;
            return acc;
          }, {});
          const inprogress = (map["Pending"] || 0) + (map["Inprogress"] || 0) + (map["In-progress"] || 0) + (map["In-transit"] || 0) + (map["At Hub"] || 0);
          const arrived = map["Arrived"] || 0;
          const delivered = map["Delivered"] || 0;
          const cancelled = map["Cancelled"] || 0;
          setPackageCounts({
            inprogress,
            arrived,
            total: inprogress + arrived + delivered + cancelled,
          });
        }
      } catch (error) {
        console.error("Failed to load packages overview:", error);
      } finally {
        setLoadingPackages(false);
      }
    };

    loadAccountData();
    loadPackagesOverview();
  }, []);

  // Stats cards data. Wallet is inactive for most customers — hide the balance
  // card for them rather than showing a wallet that isn't usable.
  const statsData = [
    ...(walletActive
      ? [
          {
            title: "Total Balance",
            value: balance ? `${balance.totalInTHB?.toLocaleString()} THB` : "---",
            icon: <TrendingUp />,
            pastel: { bg: "#e8f5e9", icon: "#66bb6a", border: "#c8e6c9", chip: "#a5d6a7", chipText: "#2e7d32" },
            change: "current",
            onClick: () => navigate("/customer-portal/wallet"),
          },
        ]
      : []),
    {
      title: "In-Transit",
      value: loadingPackages ? null : String(packageCounts.inprogress),
      icon: <LocalShipping />,
      pastel: { bg: "#fff8e1", icon: "#ffca28", border: "#ffe082", chip: "#ffe082", chipText: "#f57f17" },
      change: packageCounts.inprogress > 0 ? "in transit" : "none active",
      onClick: () => navigate("/myparcels?status=In-transit"),
    },
    {
      title: "Arrived",
      value: loadingPackages ? null : String(packageCounts.arrived),
      icon: <Inventory />,
      pastel: { bg: "#e3f2fd", icon: "#64b5f6", border: "#bbdefb", chip: "#90caf9", chipText: "#0d47a1" },
      change: packageCounts.arrived > 0 ? "at station" : "none",
      onClick: () => navigate("/myparcels?status=Arrived"),
    },
  ];

  return (
    <Box 
      sx={{ 
        p: { xs: 2, md: 3 },
        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.common.white} 100%)`,
        minHeight: "100vh"
      }}
    >
      {/* Header Section */}
      <Box mb={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight="600" color="secondary" sx={{ mb: 1 }}>
              Welcome Back!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {accountData?.customer?.name || "Customer"} • {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton 
              onClick={() => window.location.reload()}
              sx={{ 
                bgcolor: "background.paper",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "grey.100" }
              }}
            >
              <Refresh />
            </IconButton>
            <IconButton 
              sx={{ 
                bgcolor: "background.paper",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": { bgcolor: "grey.100" }
              }}
            >
              <Notifications />
            </IconButton>
          </Box>
        </Box>
        
        <Divider sx={{ opacity: 0.3 }} />
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} mb={4}>
        {statsData.map((stat, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              onClick={stat.onClick}
              sx={{
                borderRadius: 3,
                background: stat.pastel.bg,
                border: `1px solid ${stat.pastel.border}`,
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                transition: "all 0.25s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.1)"
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "rgba(255,255,255,0.6)",
                      color: stat.pastel.icon,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Chip
                    label={stat.change}
                    size="small"
                    sx={{
                      bgcolor: stat.pastel.chip,
                      color: stat.pastel.chipText,
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      border: "none",
                    }}
                  />
                </Box>
                <Typography variant="h4" fontWeight="700" mb={0.5} sx={{ color: stat.pastel.chipText }}>
                  {(loading && stat.title === "Total Balance") || stat.value === null
                    ? <LinearProgress sx={{ width: 80, height: 8, borderRadius: 4 }} />
                    : stat.value}
                </Typography>
                <Typography variant="body2" sx={{ color: stat.pastel.chipText, opacity: 0.75, fontWeight: 500 }}>
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent Packages */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: `1px solid ${theme.palette.divider}`,
          mb: 4,
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6" fontWeight="600">
              Recent Packages
            </Typography>
            <Box
              component="button"
              onClick={() => navigate("/myparcels")}
              sx={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: theme.palette.secondary.main,
                fontSize: 14,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              View all <ArrowForward sx={{ fontSize: 16 }} />
            </Box>
          </Box>

          {loadingPackages ? (
            <LinearProgress />
          ) : recentPackages.length === 0 ? (
            <Box textAlign="center" py={3}>
              <Typography variant="body2" color="text.secondary">
                You don't have any packages yet.
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider flexItem />} spacing={0}>
              {recentPackages.map((pkg) => {
                const visual = statusVisual(pkg.status);
                return (
                  <Box
                    key={pkg._id}
                    sx={{
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "grey.50" },
                      borderRadius: 1,
                      px: 1,
                    }}
                    onClick={() => {
                      setSelectedTracking(pkg.tracking);
                      setTrackingOpen(true);
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: visual.color,
                        width: 36,
                        height: 36,
                      }}
                    >
                      {visual.icon}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        noWrap
                        fontFamily="monospace"
                      >
                        {pkg.tracking}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ display: "block" }}
                      >
                        {pkg.description || "—"}
                      </Typography>
                    </Box>
                    <Box textAlign="right" flexShrink={0}>
                      <Chip
                        label={pkg.status || "Unknown"}
                        size="small"
                        sx={{
                          bgcolor: `${visual.color}1f`,
                          color: visual.color,
                          fontWeight: 500,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {pkg.updatedAt
                          ? moment(pkg.updatedAt).fromNow()
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Customer Profile */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h6" fontWeight="600">
              My Profile
            </Typography>
            <Box
              component="button"
              onClick={() => navigate("/customer-portal/myprofile")}
              sx={{
                background: "transparent", border: "none", cursor: "pointer",
                color: theme.palette.secondary.main, fontSize: 14, fontWeight: 500,
                display: "flex", alignItems: "center", gap: 0.5,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Edit <ArrowForward sx={{ fontSize: 16 }} />
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Left: Avatar + Name */}
            <Grid item xs={12} sm={3} sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <Avatar
                src={profileData?.profilePicture ? `/uploads/profiles/${profileData.profilePicture}` : null}
                sx={{
                  width: 80, height: 80,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  fontSize: 32, fontWeight: 600, mb: 1,
                }}
              >
                {profileData?.name?.charAt(0)?.toUpperCase() || accountData?.customer?.name?.charAt(0) || "C"}
              </Avatar>
              <Typography variant="subtitle1" fontWeight="700" textAlign="center">
                {profileData?.name || accountData?.customer?.name || "—"}
              </Typography>
              <Chip
                label={accountData?.validflag ? "Active" : "Inactive"}
                color={accountData?.validflag ? "success" : "default"}
                size="small"
                sx={{ mt: 0.5, fontWeight: 500 }}
              />
            </Grid>

            {/* Right: Info Fields */}
            <Grid item xs={12} sm={9}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">WhatsApp</Typography>
                  <Typography variant="body2" fontWeight="500">
                    {profileData?.whatsapp || "—"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Pickup Point</Typography>
                  <Typography variant="body2" fontWeight="500">
                    {routes.find(r => r.id === profileData?.default_route)?.name || profileData?.default_route || "—"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Member Since</Typography>
                  <Typography variant="body2" fontWeight="500">
                    {accountData?.createdAt ? new Date(accountData.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Total Packages</Typography>
                  <Typography variant="body2" fontWeight="500">
                    {loadingPackages ? "—" : `${packageCounts.total} shipments`}
                  </Typography>
                </Grid>

                {/* Delivery Addresses */}
                {profileData?.deliveryAddresses?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Home Delivery Address{profileData.deliveryAddresses.length > 1 ? `es (${profileData.deliveryAddresses.length})` : ""}
                    </Typography>
                    <Stack spacing={0.5} mt={0.5}>
                      {profileData.deliveryAddresses.slice(0, 2).map((addr, i) => (
                        <Typography key={i} variant="body2" fontWeight="500" noWrap>
                          {addr.isDefault && <Chip label="Default" size="small" color="secondary" sx={{ mr: 0.5, height: 16, fontSize: "0.65rem" }} />}
                          {addr.title}{addr.description ? ` — ${addr.description}` : ""}
                        </Typography>
                      ))}
                      {profileData.deliveryAddresses.length > 2 && (
                        <Typography variant="caption" color="text.secondary">
                          +{profileData.deliveryAddresses.length - 2} more
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                )}

                {/* Forward Addresses */}
                {profileData?.forwardAddresses?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Forward Address{profileData.forwardAddresses.length > 1 ? `es (${profileData.forwardAddresses.length})` : ""}
                    </Typography>
                    <Stack spacing={0.5} mt={0.5}>
                      {profileData.forwardAddresses.slice(0, 2).map((addr, i) => (
                        <Typography key={i} variant="body2" fontWeight="500" noWrap>
                          {addr.isDefault && <Chip label="Default" size="small" color="secondary" sx={{ mr: 0.5, height: 16, fontSize: "0.65rem" }} />}
                          {addr.logisticCompany}{addr.branch ? ` — ${addr.branch}` : ""}
                        </Typography>
                      ))}
                      {profileData.forwardAddresses.length > 2 && (
                        <Typography variant="caption" color="text.secondary">
                          +{profileData.forwardAddresses.length - 2} more
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TrackingDetailDialog
        open={trackingOpen}
        onClose={() => setTrackingOpen(false)}
        trackingNumber={selectedTracking}
        hideEditHistory
      />
    </Box>
  );
}

export default CustomerPortal;
